"use client"

/* ---------- SEO: title, description, canonical, hreflang, OG, JSON-LD ---------- */

/* ---------- Brand ---------- */
// The wordmark is never mirrored in RTL (brand rule 02).

/* ---------- Icons: 24 grid, 1.75 stroke, rounded ends ---------- */

/* ---------- Layout primitives ---------- */

/* ---------- Breadcrumbs + page header ---------- */

/* ---------- Brand graphic: the track (a continuous line connecting modules) ---------- */

/* ---------- Cards ---------- */

/* ---------- FAQ (native details/summary: keyboard + screen reader friendly) ---------- */

/* ---------- Empty state ---------- */

/* ---------- CTA ---------- */

/* ---------- Language switcher: keeps the current page ---------- */

import { useEffect, useState, type ReactNode } from "react"
import { Link, useRouter, useT } from "../lib/router"
import type { IconName } from "../content/services"

import { SITE } from "../lib/site"
export { SITE }
export function Wordmark({
  variant = "blue",
  height = 24,
  className = "h-6",
}: {
  variant?: "blue" | "white"
  height?: 22 | 24
  className?: string
}) {
  const t = useT()
  const width = height === 22 ? 150 : 164
  return (
    <img
      src={`/brand/orkest-${variant}-${height}.webp`}
      srcSet={`/brand/orkest-${variant}-${height}.webp 1x, /brand/orkest-${variant}-${height}@2x.webp 2x`}
      alt={t(SITE.name)}
      width={width}
      height={height}
      className={className}
      style={{ direction: "ltr" }}
    />
  )
}
const paths: Record<IconName | "arrow" | "check" | "menu" | "close" | "plus" | "shield" | "eye" | "user" | "mail" | "pin", ReactNode> =
  {
    branding: (
      <>
        <path d="m12 3 7 7-9 9-7-7 9-9Z" />
        <path d="m12 3 1 8 6-1M13 11l-6 6" />
        <circle cx="13" cy="11" r="1" />
      </>
    ),
    presence: (
      <>
        <rect x="3" y="4" width="18" height="13" rx="2.5" />
        <path d="M8 21h8M12 17v4M3 8h18" />
      </>
    ),
    code: (
      <>
        <path d="m8 8-4 4 4 4M16 8l4 4-4 4M13.5 5l-3 14" />
      </>
    ),
    mobile: (
      <>
        <rect x="6.5" y="2.5" width="11" height="19" rx="2.5" />
        <path d="M11 18h2" />
      </>
    ),
    erp: (
      <>
        <rect x="3" y="3" width="7.5" height="7.5" rx="2" />
        <rect x="13.5" y="3" width="7.5" height="7.5" rx="2" />
        <rect x="3" y="13.5" width="7.5" height="7.5" rx="2" />
        <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="2" />
      </>
    ),
    workflow: (
      <>
        <circle cx="5.5" cy="6" r="2.5" />
        <circle cx="18.5" cy="18" r="2.5" />
        <path d="M8 6h6a4 4 0 0 1 4 4v0a4 4 0 0 1-4 4h-4a4 4 0 0 0-4 4v0" />
      </>
    ),
    ai: (
      <>
        <rect x="5" y="5" width="14" height="14" rx="3" />
        <path d="M9 2.5V5M15 2.5V5M9 19v2.5M15 19v2.5M2.5 9H5M2.5 15H5M19 9h2.5M19 15h2.5M9.5 14.5l2.5-5 2.5 5M10.3 13h3.4" />
      </>
    ),
    education: (
      <>
        <path d="M12 6C9 4 6 3.5 3 4v15c3-.5 6 0 9 2 3-2 6-2.5 9-2V4c-3-.5-6 0-9 2Z" />
        <path d="M12 6v15M6 8l3 1M6 12l3 1M15 9l3-1M15 13l3-1" />
      </>
    ),
    cloud: (
      <>
        <path d="M7 18.5h10a4 4 0 0 0 .6-7.96A5.5 5.5 0 0 0 6.9 10 4.25 4.25 0 0 0 7 18.5Z" />
      </>
    ),
    arrow: <path d="M5 12h14M13 6l6 6-6 6" />,
    check: <path d="m5 12.5 4.5 4.5L19 7.5" />,
    menu: <path d="M4 7h16M4 12h16M4 17h16" />,
    close: <path d="M6 6l12 12M18 6 6 18" />,
    plus: <path d="M12 5v14M5 12h14" />,
    shield: (
      <path d="M12 3 5 6v5.5c0 4.3 3 8 7 9.5 4-1.5 7-5.2 7-9.5V6l-7-3Z" />
    ),
    eye: (
      <>
        <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
        <circle cx="12" cy="12" r="2.75" />
      </>
    ),
    user: (
      <>
        <circle cx="12" cy="8" r="3.5" />
        <path d="M5 20a7 7 0 0 1 14 0" />
      </>
    ),
    mail: (
      <>
        <rect x="3" y="5" width="18" height="14" rx="2.5" />
        <path d="m3.5 7 8.5 6 8.5-6" />
      </>
    ),
    pin: (
      <>
        <path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11Z" />
        <circle cx="12" cy="10" r="2.3" />
      </>
    ),
  }
/** Explicit codepoints avoid pseudo-element font rendering inconsistencies. */
const iconGlyphs = {
  "mark-o": "\ue900",
  "track-flow": "\ue901",
  "rhythm-e": "\ue902",
  "harmony": "\ue903",
  "presence": "\ue904",
  "branding": "\ue905",
  "code": "\ue906",
  "mobile": "\ue907",
  "erp": "\ue908",
  "workflow": "\ue909",
  "ai": "\ue90a",
  "education": "\ue90b",
  "cloud": "\ue90c",
  "understand": "\ue90d",
  "step-design": "\ue90e",
  "step-build": "\ue90f",
  "step-connect": "\ue910",
  "step-improve": "\ue911",
  "agencies": "\ue912",
  "real-estate": "\ue913",
  "ecommerce": "\ue914",
  "professional": "\ue915",
  "arrow": "\ue916",
  "arrow-left": "\ue917",
  "arrow-up-right": "\ue918",
  "chevron-down": "\ue919",
  "check": "\ue91a",
  "plus": "\ue91b",
  "menu": "\ue91c",
  "close": "\ue91d",
  "shield": "\ue91e",
  "support": "\ue91f",
  "eye": "\ue920",
  "user": "\ue921",
  "mail": "\ue922",
  "pin": "\ue923",
  "globe": "\ue924",
  "route-branch": "\ue925",
  "analytics": "\ue926",
  "partner-handshake": "\ue927",
  "facebook": "\ue928",
  "linkedin": "\ue929",
  "instagram": "\ue92a",
  "github": "\ue92b",
  "arrow-up": "\ue92c",
  "search": "\ue92d",
  "phone": "\ue92e",
  "calendar": "\ue92f",
  "download": "\ue930",
  "external-link": "\ue931",
  "filter": "\ue932",
  "clock": "\ue933",
  "settings": "\ue934",
  "bell": "\ue935",
  "chevron-right": "\ue936",
  "chevron-left": "\ue937",
  "refresh": "\ue938",
  "copy": "\ue939",
} as const
export type AnyIcon = keyof typeof paths | keyof typeof iconGlyphs
const directionalIcons = new Set<AnyIcon>(["arrow", "arrow-left", "workflow"])
export function Icon({
  name,
  className = "size-6",
  flip,
}: {
  name: AnyIcon
  className?: string
  flip?: boolean
}) {
  const mirror = flip ?? directionalIcons.has(name)
  return (
    <span
      aria-hidden="true"
      className={`ork-site-icon ${mirror ? "ork-rtl-flip" : ""} ${className}`}
    >
      {iconGlyphs[name]}
    </span>
  )
}
export function Container({
  children,
  className = "",
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={`mx-auto w-full max-w-[1280px] px-6 md:px-10 lg:px-16 ${className}`}
    >
      {children}
    </div>
  )
}

type BtnProps = {
  to: string
  children: ReactNode
  variant?: "primary" | "secondary" | "ghost" | "inverse"
  className?: string
}
export function Button({
  to,
  children,
  variant = "primary",
  className = "",
}: BtnProps) {
  const styles = {
    primary: "bg-blue text-white hover:bg-ink",
    secondary:
      "bg-white text-blue ring-1 ring-slate hover:ring-blue hover:bg-mist",
    ghost: "text-blue hover:bg-ice",
    inverse: "bg-white text-blue hover:bg-ice",
  }[variant]
  return (
    <Link
      to={to}
      className={`group inline-flex min-h-12 items-center justify-center gap-2 rounded-r8 px-5 text-base font-medium transition-colors ${styles} ${className}`}
    >
      {children}
      <Icon
        name="arrow"
        flip
        className="size-5 transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5"
      />
    </Link>
  )
}

export function Eyebrow({
  children,
  light,
}: {
  children: ReactNode
  light?: boolean
}) {
  return (
    <p
      className={`flex items-center gap-3 text-sm font-medium uppercase tracking-[0.12em] rtl:tracking-normal ${
        light ? "text-white" : "text-blue"
      }`}
    >
      <span className={`h-[3px] w-8 shrink-0 ${light ? "bg-white/50" : "bg-blue"}`} />
      {children}
    </p>
  )
}

export function SectionHeading({
  eyebrow,
  title,
  lead,
  light,
  className = "",
}: {
  eyebrow?: string
  title: string
  lead?: string
  light?: boolean
  className?: string
}) {
  return (
    <div className={`max-w-3xl ${className}`}>
      {eyebrow && <Eyebrow light={light}>{eyebrow}</Eyebrow>}
      <h2
        className={`brand-h2 mt-4 font-medium tracking-tight ${
          light ? "text-white" : "text-ink"
        }`}
      >
        {title}
      </h2>
      {lead && (
        <p className={`mt-5 text-lg ${light ? "text-white" : "text-slate"}`}>
          {lead}
        </p>
      )}
    </div>
  )
}

export function Section({
  children,
  tone = "white",
  id,
  className = "",
}: {
  children: ReactNode
  tone?: "white" | "mist" | "blue" | "ink"
  id?: string
  className?: string
}) {
  const bg = {
    white: "bg-white",
    mist: "bg-mist",
    blue: "bg-blue text-white",
    ink: "bg-ink text-white",
  }[tone]
  return (
    <section id={id} className={`${bg} py-20 md:py-28 ${className}`}>
      <Container>{children}</Container>
    </section>
  )
}
export type Crumb = { label: string; to?: string }
export function Breadcrumbs({ items }: { items: Crumb[] }) {
  const t = useT()
  const all: Crumb[] = [
    { label: t({ en: "Home", ar: "الرئيسية" }), to: "/" },
    ...items,
  ]
  return (
    <nav aria-label={t({ en: "Breadcrumb", ar: "مسار التنقل" })}>
      <ol className="flex flex-wrap items-center gap-2 text-sm text-slate">
        {all.map((c, i) => (
          <li key={i} className="flex items-center gap-2">
            {c.to && i < all.length - 1 ? (
              <Link to={c.to} className="hover:text-blue">
                {c.label}
              </Link>
            ) : (
              <span aria-current="page" className="text-ink">
                {c.label}
              </span>
            )}
            {i < all.length - 1 && (
              <span aria-hidden className="text-line">
                /
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  )
}

export function PageHeader({
  crumbs,
  eyebrow,
  title,
  lead,
  icon,
  children,
}: {
  crumbs: Crumb[]
  eyebrow?: string
  title: string
  lead?: string
  icon?: AnyIcon
  children?: ReactNode
}) {
  return (
    <header className="relative overflow-hidden border-b border-line bg-white">
      <Track className="pointer-events-none absolute -end-24 top-10 hidden h-auto w-[620px] opacity-[0.08] lg:block" />
      <Container className="relative pb-16 pt-10 md:pb-20">
        <Breadcrumbs items={crumbs} />
        <div className="mt-12 max-w-4xl rise">
          {icon && (
            <span className="mb-6 inline-flex size-14 items-center justify-center rounded-r16 bg-ice text-blue">
              <Icon name={icon} className="size-7" />
            </span>
          )}
          {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
          <h1 className="brand-h1 mt-4 font-medium tracking-tight text-ink">
            {title}
          </h1>
          {lead && (
            <p className="mt-6 max-w-2xl text-lg text-slate md:text-xl md:leading-8">
              {lead}
            </p>
          )}
          {children && (
            <div className="mt-8 flex flex-wrap gap-3">{children}</div>
          )}
        </div>
      </Container>
    </header>
  )
}
/** Original EN-17 artwork, composed at its Figma coordinates without mirroring. */
export function Track({
  className = "",
  surface = false,
}: {
  className?: string
  surface?: boolean
}) {
  return (
    <svg
      viewBox="0 0 520 210"
      width="520"
      height="210"
      fill="none"
      className={className}
      aria-hidden="true"
      focusable="false"
      style={{ overflow: "hidden" }}
    >
      {surface && (
        <image href="/brand/track-surface.svg" width="520" height="210" />
      )}
      <image
        href="/brand/track-main.svg"
        x="83.15625"
        y="29.84337"
        width="359.214"
        height="137.053"
      />
      <image
        href="/brand/track-lower.svg"
        x="83.15625"
        y="71.84343"
        width="359.214"
        height="137.053"
        opacity="0.22"
      />
      <image
        href="/brand/track-upper.svg"
        x="260"
        y="-22.10416"
        width="182.37"
        height="124.891"
        opacity="0.5"
      />
    </svg>
  )
}
export function LinkCard({
  to,
  icon,
  eyebrow,
  title,
  body,
  footer,
  className = "",
}: {
  to: string
  icon?: AnyIcon
  eyebrow?: string
  title: string
  body: string
  footer?: ReactNode
  className?: string
}) {
  const t = useT()
  return (
    <Link
      to={to}
      className={`group flex h-full flex-col rounded-r16 border border-line bg-white p-7 transition-colors hover:border-blue ${className}`}
    >
      {icon && (
        <span className="inline-flex size-12 items-center justify-center rounded-r8 bg-ice text-blue transition-colors group-hover:bg-blue group-hover:text-white">
          <Icon name={icon} />
        </span>
      )}
      {eyebrow && (
        <p className={`text-sm font-medium text-slate ${icon ? "mt-6" : ""}`}>
          {eyebrow}
        </p>
      )}
      <h3
        className={`text-xl font-medium text-ink ${
          icon && !eyebrow ? "mt-6" : "mt-2"
        }`}
      >
        {title}
      </h3>
      <p className="mt-3 flex-1 text-base leading-7 text-slate">{body}</p>
      {footer}
      <span className="mt-6 inline-flex items-center gap-2 text-base font-medium text-blue">
        {t({ en: "Learn more", ar: "اعرف المزيد" })}
        <Icon
          name="arrow"
          flip
          className="size-5 transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1"
        />
      </span>
    </Link>
  )
}

export function Pill({
  children,
  tone = "ice",
}: {
  children: ReactNode
  tone?: "ice" | "line" | "amber"
}) {
  const c = {
    ice: "bg-ice text-blue",
    line: "bg-mist text-slate ring-1 ring-line",
    amber: "bg-[#FFF4E0] text-[#8A5300]",
  }[tone]
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${c}`}
    >
      {children}
    </span>
  )
}

export function CheckList({
  items,
  columns = 2,
}: {
  items: string[]
  columns?: 1 | 2
}) {
  return (
    <ul
      className={`grid gap-x-8 gap-y-4 ${
        columns === 2 ? "sm:grid-cols-2" : ""
      }`}
    >
      {items.map((i) => (
        <li key={i} className="flex gap-3 text-base leading-7 text-ink">
          <Icon name="check" className="mt-1 size-5 shrink-0 text-blue" />
          {i}
        </li>
      ))}
    </ul>
  )
}
export function FAQList({ items }: { items: { q: string; a: string }[] }) {
  return (
    <div className="divide-y divide-line border-y border-line">
      {items.map((f) => (
        <details key={f.q} className="group py-6">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-6 text-lg font-medium text-ink [&::-webkit-details-marker]:hidden">
            {f.q}
            <Icon
              name="plus"
              className="size-5 shrink-0 text-blue transition-transform group-open:rotate-45"
            />
          </summary>
          <p className="mt-4 max-w-3xl text-base leading-7 text-slate">{f.a}</p>
        </details>
      ))}
    </div>
  )
}
export function EmptyState({
  title,
  body,
  action,
}: {
  title: string
  body: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-start gap-4 rounded-r24 border border-dashed border-line bg-mist p-10">
      <img
        src="/brand/orkest-mark-blue-32.webp"
        srcSet="/brand/orkest-mark-blue-32.webp 1x, /brand/orkest-mark-blue-32@2x.webp 2x"
        alt=""
        width={34}
        height={32}
        className="h-8 opacity-80"
      />
      <h3 className="text-xl font-medium text-ink">{title}</h3>
      <p className="max-w-xl text-base text-slate">{body}</p>
      {action}
    </div>
  )
}
export function CTASection({
  title,
  body,
  eyebrow,
  primaryLabel,
  id,
}: {
  title?: string
  body?: string
  eyebrow?: string
  primaryLabel?: string
  id?: string
}) {
  const t = useT()
  return (
    <section id={id} className="bg-white py-20 md:py-28">
      <Container>
        <div className="relative overflow-hidden rounded-r24 bg-blue px-8 py-14 text-white md:px-16 md:py-20">
          <Track className="pointer-events-none absolute -bottom-10 -end-10 h-auto w-[480px] opacity-[0.12] [filter:brightness(0)_invert(1)]" />
          <div className="relative max-w-2xl">
            {eyebrow && <Eyebrow light>{eyebrow}</Eyebrow>}
            <h2 className={"text-3xl font-medium tracking-tight md:text-[44px] md:leading-[52px] " + (eyebrow ? "mt-5" : "")}>
              {title ??
                t({
                  en: "Let\u2019s talk about your business.",
                  ar: "لنتحدث عن أعمالك.",
                })}
            </h2>
            <p className="mt-5 text-lg text-white">
              {body ??
                t({
                  en: "Tell us where your operations slow down. We will suggest a practical first step — whether it is a website, a system, or an automated workflow.",
                  ar: "أخبرنا أين يتباطأ العمل لديك، وسنقترح خطوة أولى عملية، سواء كانت موقعًا أو نظامًا أو مسار عمل مؤتمتًا.",
                })}
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Button to="/start-project" variant="inverse">
                {primaryLabel ?? t({ en: "Start a project", ar: "ابدأ مشروعك" })}
              </Button>
              <Link
                to="/contact"
                className="inline-flex min-h-12 items-center rounded-r8 px-5 font-medium text-white ring-1 ring-white/40 hover:bg-white/10"
              >
                {t({ en: "Book a consultation", ar: "احجز استشارة" })}
              </Link>
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}
export function LanguageSwitcher({ className = "" }: { className?: string }) {
  const { route } = useRouter()
  const other = route.locale === "ar" ? "en" : "ar"

  return (
    <Link
      to={route.path}
      locale={other}
      hrefLang={other}
      lang={other}
      aria-label={other === "ar" ? "عربي" : "EN"}
      className={`inline-flex items-center text-base font-medium text-ink hover:text-blue transition-colors ${className}`}
    >
      {other === "ar" ? "عربي" : "EN"}
    </Link>
  )
}

export function useMediaReduced() {
  const [r, setR] = useState(false)
  useEffect(
    () => setR(window.matchMedia("(prefers-reduced-motion: reduce)").matches),
    [],
  )
  return r
}
